import React, { useCallback } from 'react';
import {
  Box, Button, Chip, LinearProgress, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTranslation } from 'react-i18next';
import { PackingOrderDto } from '../../services/packingService';
import PackingChannelBanner from './PackingChannelBanner';
import WmsScannerPanel from '../../shared/WmsScannerPanel';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import { matchesScannedValue } from '../../shared/scanMatching';
import { WMS_TYPE_CHIP_SX } from '../../shared/wmsTypeChip';

interface PackingCartOrdersProps {
  cartCode: string;
  marketplaceSource?: string;
  // Paketlenmeyi bekleyen (kalan) siparisler.
  orders: PackingOrderDto[];
  totalCount: number;
  // Satira tiklanarak secim: siparis detay ekranina gider.
  onSelectOrder: (order: PackingOrderDto) => void;
  // Barkod okutularak eslesme: dogrudan etiket yazdirma ekranina gider.
  onScanOrder: (order: PackingOrderDto) => void;
  onBack: () => void;
}

const PackingCartOrders: React.FC<PackingCartOrdersProps> = ({
  cartCode, marketplaceSource, orders, totalCount, onSelectOrder, onScanOrder, onBack,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();
  const packedCount = Math.max(0, totalCount - orders.length);
  const progress = totalCount > 0 ? (packedCount / totalCount) * 100 : 0;

  // Okutulan barkodu arabadaki kalan siparislerle eslestir: kalemlerinden herhangi biri
  // (barkod / SKU / urun) esen ilk siparisi dogrudan etiket adimina gonder.
  const handleScan = useCallback((barcode: string) => {
    const value = (barcode || '').trim();
    if (!value) return;
    const matched = orders.find((o) =>
      (o.items || []).some((it) =>
        matchesScannedValue(value, it.barcode) ||
        matchesScannedValue(value, it.sku) ||
        matchesScannedValue(value, it.productId),
      ),
    );
    if (!matched) {
      notify({ type: 'error', message: t('screens.packing.cartOrders.notInCart') });
      return;
    }
    onScanOrder(matched);
  }, [orders, onScanOrder, notify, t]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary' }}>
          {t('screens.packing.common.back')}
        </Button>
      </Box>

      <PackingChannelBanner source={marketplaceSource} />

      <WmsScannerPanel
        title={t('screens.packing.cartOrders.scanTitle')}
        placeholder={t('screens.packing.cartOrders.scanPlaceholder')}
        helperText={t('screens.packing.cartOrders.scanHelper')}
        onScan={handleScan}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ShoppingCartIcon sx={{ color: '#7b1fa2' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('screens.packing.cartOrders.cartLabel', { code: cartCode })}
        </Typography>
        <Chip
          size="small"
          color="secondary"
          variant="outlined"
          label={t('screens.packing.cartOrders.progress', { done: packedCount, total: totalCount })}
        />
      </Box>

      <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('screens.packing.cartOrders.listTitle', { count: orders.length })}
      </Typography>

      <Paper sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('screens.packing.cartOrders.colOrderNo')}</TableCell>
              <TableCell>{t('screens.packing.cartOrders.colCustomer')}</TableCell>
              <TableCell align="center">{t('screens.packing.cartOrders.colProduct')}</TableCell>
              <TableCell align="center">{t('screens.packing.cartOrders.colType')}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow
                key={order.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onSelectOrder(order)}
              >
                <TableCell sx={{ fontWeight: 600 }}>{order.orderNumber}</TableCell>
                <TableCell>{order.customerName}</TableCell>
                <TableCell align="center">{t('screens.packing.common.itemsCount', { count: order.itemCount })}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={order.orderType === 'Single' ? t('screens.packing.common.typeSingle') : t('screens.packing.common.typeMulti')}
                    size="small"
                    color={order.orderType === 'Single' ? 'info' : 'secondary'}
                    sx={WMS_TYPE_CHIP_SX}
                  />
                </TableCell>
                <TableCell align="right">
                  <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <WmsNotification notification={notification} onClose={clear} />
    </Box>
  );
};

export default PackingCartOrders;
